// Tiptap core imports
import { mergeAttributes } from '@tiptap/core';
import { generateHTML } from '@tiptap/html';

import { DocumentContentItem } from '@distribute/shared/types';

import {
  createBooleanAttribute,
  createSerializedAttribute,
  withUndefinedDefaults,
} from '@distribute/shared/tiptap/utils';

// Tiptap extensions
import Document from '@tiptap/extension-document';
import Text from '@tiptap/extension-text';
import Paragraph from '@tiptap/extension-paragraph';
import HardBreak from '@tiptap/extension-hard-break';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Strike from '@tiptap/extension-strike';
import Heading from '@tiptap/extension-heading';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Blockquote from '@tiptap/extension-blockquote';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import Code from '@tiptap/extension-code';

// Custom extensions
import {
  ButtonExt,
  CalloutExt,
  CodeBlockExt,
  ColumnBlockExt,
  ColumnExt,
  ContactCardExt,
  CustomLinkExt,
  CustomTableExt,
  CustomTableCellExt,
  CustomTaskListExt,
  EmbedContentBlockExt,
  EmbedContentExt,
  ExtendedHorizontalRuleExt,
  FilePreviewExt,
  IframeExt,
  ImageExt,
  ResizeableFigureExt,
  SnippetBlockExt,
  TimelineExt,
  TimelineItemExt,
  ToggleListExt,
  ToggleListItemContentExt,
  ToggleListItemExt,
  TodoListExt,
  TodoListItemExt,
  VideoExt,
  VideoRecordExt,
  VimeoExt,
  YoutubeExt,
  PdfComponentExt,
} from '@distribute/shared/tiptap/extensions';

// Comments
import {
  BlockThreadNode,
  InlineThreadMark,
} from '@distribute/shared/tiptap/extensions';

const XmlParagraph = Paragraph.extend({
  parseHTML() {
    return [
      {
        tag: 'Paragraph',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'Paragraph',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },
});

const XmlHardBreak = HardBreak.extend({
  parseHTML() {
    return [
      {
        tag: 'HardBreak',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'HardBreak',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
    ];
  },
});

const XmlHeading = Heading.extend({
  parseHTML() {
    return [{ tag: 'Heading' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      `Heading`,
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        level: node.attrs.level,
      }),
      0,
    ];
  },
}).configure({ levels: [1, 2, 3] });

const XmlBold = Bold.extend({
  parseHTML() {
    return [
      {
        tag: 'Bold',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'Bold',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },
});

const XmlItalic = Italic.extend({
  parseHTML() {
    return [
      {
        tag: 'Italic',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'Italic',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },
});

const XmlStrike = Strike.extend({
  parseHTML() {
    return [
      {
        tag: 'Strike',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'Strike',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },
});

const XmlUnderline = Underline.extend({
  parseHTML() {
    return [
      {
        tag: 'Underline',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'Underline',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },
});

const XmlCode = Code.extend({
  parseHTML() {
    return [
      {
        tag: 'Code',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'Code',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },
});

const XmlBulletList = BulletList.extend({
  parseHTML() {
    return [
      {
        tag: 'BulletList',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'BulletList',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },
});

const XmlOrderedList = OrderedList.extend({
  parseHTML() {
    return [
      {
        tag: 'OrderedList',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'OrderedList',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },
});

const XmlListItem = ListItem.extend({
  parseHTML() {
    return [
      {
        tag: 'ListItem',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'ListItem',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },
});

const XmlTextStyle = TextStyle.extend({
  parseHTML() {
    return [
      {
        tag: 'TextStyle',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'TextStyle',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },
});

const XmlHighlight = Highlight.extend({
  parseHTML() {
    return [{ tag: 'Highlight' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'Highlight',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },
});

const XmlBlockquote = Blockquote.extend({
  parseHTML() {
    return [{ tag: 'Blockquote' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'Blockquote',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },
});

const XmlExtendedHorizontalRule = ExtendedHorizontalRuleExt.extend({
  parseHTML() {
    return [{ tag: 'HorizontalRule' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'HorizontalRule',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
    ];
  },
});

export const XmlToggleList = ToggleListExt.extend({
  parseHTML() {
    return [{ tag: 'ToggleList' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'ToggleList',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },
});

export const XmlToggleListItem = ToggleListItemExt.extend({
  parseHTML() {
    return [{ tag: 'ToggleListItem' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'ToggleListItem',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },
});

export const XmlToggleListItemContent = ToggleListItemContentExt.extend({
  parseHTML() {
    return [{ tag: 'ToggleListItemContent' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['ToggleListItemContent', HTMLAttributes, 0];
  },
});

export const XmlTimeline = TimelineExt.extend({
  parseHTML() {
    return [{ tag: 'Timeline' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['Timeline', HTMLAttributes, 0];
  },
});

export const XmlTimelineItem = TimelineItemExt.extend({
  addAttributes() {
    const parentAttributes = this.parent?.() ?? {};

    return withUndefinedDefaults({
      ...parentAttributes,
      isDone: createBooleanAttribute('isDone'),
    });
  },

  parseHTML() {
    return [{ tag: 'TimelineItem' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['TimelineItem', HTMLAttributes, 0];
  },
});

export const XmlCustomTaskList = CustomTaskListExt.extend({
  addAttributes() {
    return withUndefinedDefaults(this.parent?.() ?? {});
  },

  parseHTML() {
    return [{ tag: 'CustomTaskList' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return ['CustomTaskList', HTMLAttributes, `{{ ${node.attrs.id} }}`];
  },
});

export const XmlContactCard = ContactCardExt.extend({
  addAttributes() {
    return withUndefinedDefaults(this.parent?.() ?? {});
  },

  parseHTML() {
    return [{ tag: 'ContactCard' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'ContactCard',
      mergeAttributes(this.options?.HTMLAttributes, HTMLAttributes),
    ];
  },
});

const XmlCustomLink = CustomLinkExt.extend({
  addAttributes() {
    return withUndefinedDefaults(this.parent?.() ?? {});
  },

  parseHTML() {
    return [{ tag: 'CustomLink' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return ['CustomLink', HTMLAttributes, node.attrs.text];
  },
});

export const XmlCallout = CalloutExt.extend({
  addAttributes() {
    return withUndefinedDefaults(this.parent?.() ?? {});
  },

  parseHTML() {
    return [{ tag: 'Callout' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['Callout', HTMLAttributes, 0];
  },
});

export const XmlSnippetBlock = SnippetBlockExt.extend({
  addAttributes() {
    return withUndefinedDefaults(this.parent?.() ?? {});
  },

  parseHTML() {
    return [{ tag: 'SnippetBlock' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return ['SnippetBlock', HTMLAttributes, `{{ ${node.attrs.id} }}`];
  },
});

const XmlCustomTable = CustomTableExt.extend({
  addAttributes() {
    return withUndefinedDefaults({
      ...this.parent?.(),
      selected: createBooleanAttribute('selected'),
    });
  },

  parseHTML() {
    return [{ tag: 'CustomTable' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'CustomTable',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },
});

const XmlCustomTableCell = CustomTableCellExt.extend({
  addAttributes() {
    return withUndefinedDefaults({
      ...this.parent?.(),
      tableCellAlign: { default: undefined },
      tableCellVerticalAlign: { default: undefined },
    });
  },

  parseHTML() {
    return [{ tag: 'TableCell' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'TableCell',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },
});

const XmlTableHeader = TableHeader.extend({
  addAttributes() {
    return withUndefinedDefaults(this.parent?.() ?? {});
  },

  parseHTML() {
    return [{ tag: 'TableHeader' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'TableHeader',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },
});

const XmlTableRow = TableRow.extend({
  addAttributes() {
    return withUndefinedDefaults(this.parent?.() ?? {});
  },

  parseHTML() {
    return [{ tag: 'TableRow' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'TableRow',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },
});

export const XmlFilePreview = FilePreviewExt.extend({
  addAttributes() {
    return withUndefinedDefaults(this.parent?.() ?? {});
  },

  parseHTML() {
    return [{ tag: 'FilePreview' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['FilePreview', HTMLAttributes, `${HTMLAttributes.fileName}`];
  },
});

const XmlImage = ImageExt.extend({
  addAttributes() {
    return withUndefinedDefaults(this.parent?.() ?? {});
  },

  parseHTML() {
    return [
      {
        tag: 'CustomImage',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['CustomImage', { ...HTMLAttributes }];
  },
});

const XmlVideo = VideoExt.extend({
  addAttributes() {
    return withUndefinedDefaults(this.parent?.() ?? {});
  },

  parseHTML() {
    return [{ tag: 'CustomVideo' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['CustomVideo', HTMLAttributes];
  },
});

export const XmlVideoRecord = VideoRecordExt.extend({
  addAttributes() {
    return withUndefinedDefaults(this.parent?.() ?? {});
  },

  parseHTML() {
    return [{ tag: 'VideoRecord' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['VideoRecord', HTMLAttributes];
  },
});

const XmlResizeableFigure = ResizeableFigureExt.extend({
  addAttributes() {
    return withUndefinedDefaults(this.parent?.() ?? {});
  },

  parseHTML() {
    return [{ tag: 'ResizeableFigure' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['ResizeableFigure', HTMLAttributes, 0];
  },
});

export const XmlIframe = IframeExt.extend({
  addAttributes() {
    return withUndefinedDefaults(this.parent?.() ?? {});
  },

  parseHTML() {
    return [{ tag: 'Iframe' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['Iframe', HTMLAttributes];
  },
});

const XmlYoutube = YoutubeExt.extend({
  addAttributes() {
    return withUndefinedDefaults(this.parent?.() ?? {});
  },

  parseHTML() {
    return [{ tag: 'Youtube' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'Youtube',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
    ];
  },
});

const XmlPdfComponent = PdfComponentExt.extend({
  addAttributes() {
    return withUndefinedDefaults(this.parent?.() ?? {});
  },

  parseHTML() {
    return [{ tag: 'PdfComponent' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'PdfComponent',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
    ];
  },
});

const XmlVimeo = VimeoExt.extend({
  addAttributes() {
    return withUndefinedDefaults(this.parent?.() ?? {});
  },

  parseHTML() {
    return [{ tag: 'Vimeo' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'Vimeo',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
    ];
  },
});

const XmlColumnBlock = ColumnBlockExt.extend({
  parseHTML() {
    return [{ tag: 'ColumnBlock' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['ColumnBlock', HTMLAttributes, 0];
  },
});

const XmlColumn = ColumnExt.extend({
  addAttributes() {
    return withUndefinedDefaults(this.parent?.() ?? {});
  },

  parseHTML() {
    return [{ tag: 'Column' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['Column', HTMLAttributes, 0];
  },
});

export const XmlTodoListItem = TodoListItemExt.extend({
  parseHTML() {
    return [{ tag: 'TodoListItem' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'TodoListItem',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },
});

export const XmlTodoList = TodoListExt.extend({
  parseHTML() {
    return [{ tag: 'TodoList' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'TodoList',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },
});

const XmlButton = ButtonExt.extend({
  addAttributes() {
    const parentAttributes = this.parent?.() ?? {};

    return withUndefinedDefaults({
      ...parentAttributes,
      edited: createBooleanAttribute('edited'),
      errors: createSerializedAttribute('errors'),
    });
  },

  parseHTML() {
    return [
      {
        tag: 'Button',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['Button', mergeAttributes(HTMLAttributes)];
  },
});

const XmlEmbedContentBlock = EmbedContentBlockExt.extend({
  parseHTML() {
    return [{ tag: 'EmbedContentBlock' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['EmbedContentBlock', HTMLAttributes, 0];
  },
});

const XmlEmbedContent = EmbedContentExt.extend({
  addAttributes() {
    return withUndefinedDefaults(this.parent?.() ?? {});
  },

  parseHTML() {
    return [{ tag: 'EmbedContent' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['EmbedContent', HTMLAttributes];
  },
});

const XmlCodeBlock = CodeBlockExt.extend({
  addAttributes() {
    return withUndefinedDefaults(this.parent?.() ?? {});
  },

  parseHTML() {
    return [{ tag: 'CodeBlock' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'CodeBlock',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },
});

const XmlCommentBlockThread = BlockThreadNode.extend({
  parseHTML() {
    return [{ tag: 'CommentBlockThread' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'CommentBlockThread',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },
});
const XmlCommentInlineMark = InlineThreadMark.extend({
  parseHTML() {
    return [{ tag: 'CommentInlineMark' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'CommentInlineMark',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },
});

export const xmlExtensions = [
  Document,
  Text,
  Color,
  XmlParagraph,
  XmlHardBreak,
  XmlHeading.configure({ levels: [1, 2, 3] }),
  XmlBold,
  XmlItalic,
  XmlStrike,
  XmlUnderline,
  XmlCode,
  XmlBulletList,
  XmlOrderedList,
  XmlListItem,
  XmlTextStyle,
  XmlHighlight.configure({
    multicolor: true,
    HTMLAttributes: {
      class: 'highlighted',
    },
  }),
  XmlBlockquote,
  TextAlign.configure({
    types: ['heading', 'paragraph', 'listItem'],
  }),
  XmlExtendedHorizontalRule,
  XmlToggleList,
  XmlToggleListItem,
  XmlToggleListItemContent,
  XmlTimeline,
  XmlTimelineItem,
  XmlCustomTaskList,
  XmlContactCard,
  XmlCustomLink,
  XmlCallout,
  XmlSnippetBlock,
  XmlCustomTable,
  XmlCustomTableCell,
  XmlTableHeader,
  XmlTableRow,
  XmlFilePreview,
  XmlImage,
  XmlVideo,
  XmlVideoRecord,
  XmlResizeableFigure,
  XmlIframe,
  XmlYoutube.configure({ controls: true, allowFullscreen: true }),
  XmlPdfComponent,
  XmlVimeo.configure({ controls: true, allowFullscreen: true }),
  XmlColumnBlock,
  XmlColumn,
  XmlTodoListItem.configure({
    nested: false,
  }),
  XmlTodoList,
  XmlButton,
  XmlEmbedContentBlock,
  XmlEmbedContent,
  XmlCodeBlock,
  XmlCommentBlockThread,
  XmlCommentInlineMark,
];

type GenerateXmlJSXProps = {
  title: string;
  tabs: DocumentContentItem[];
  isBranded: boolean;
  currentTabId: number;
  userName: string;
  brandCompanyName: string | null;
  brandCompanyInfo: string | null;
};

export const generateXmlJSX = ({
  title,
  tabs,
  currentTabId,
  isBranded,
  userName,
  brandCompanyName,
  brandCompanyInfo,
}: GenerateXmlJSXProps) => {
  const pageContent = tabs.map((content) => {
    return `
    <Tab id='${content.id}' name='${content.name}'>
      ${generateHTML(content.contentJson, xmlExtensions)}
    </Tab>
  `;
  });
  const metadata = `
  <Metadata>
    <Title>${title}</Title>
    <CurrentTabId>${currentTabId}</CurrentTabId>
    <IsBranded>${isBranded}</IsBranded>
    <UserName>${userName}</UserName>
    <UserCompany>${brandCompanyName ?? ''}</UserCompany>
    <UserCompanyInfo>${brandCompanyInfo ?? ''}</UserCompanyInfo>
  </Metadata>
  `;
  const pageWrapped = `${pageContent.join('')}${metadata}`;
  return pageWrapped;
};
